/* ───────────────────────────────────────────────────────────────────────────
 * useLcsLeaseOverlay — React hook wrapping fetchLcsLeaseOverlay + view-model.
 *
 * Returns: {view, raw, loading, error, refetch}
 *   view   — output of buildLcsViewModel(overlayJson) when ready
 *   raw    — the unmodified overlay JSON
 *
 * Auto-refetches on leaseId/horizon/streams changes. Cancels in-flight
 * requests via AbortController to avoid race-conditions on rapid switching.
 *
 * Rollback-safe: this hook is OPT-IN. Existing ForecastDashboard /
 * ForecastDashboardDecisionUI continue to work unchanged.
 * ──────────────────────────────────────────────────────────────────────── */
import { useEffect, useState, useCallback, useRef } from "react";
import { fetchLcsLeaseOverlay } from "./lcsClient";
import { buildLcsViewModel } from "./lcsViewModel";

const DEFAULT_STREAMS = ["Oil", "Gas", "Water"];

export function useLcsLeaseOverlay({ leaseId, horizon = 60, streams = DEFAULT_STREAMS, enabled = true } = {}) {
  const [state, setState] = useState({
    view: null, raw: null, loading: false, error: null,
  });
  const abortRef = useRef(null);
  const tickRef = useRef(0);

  const run = useCallback(async () => {
    if (!enabled || !leaseId) {
      setState({ view: null, raw: null, loading: false, error: null });
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    const ctl = new AbortController();
    abortRef.current = ctl;
    const tick = ++tickRef.current;

    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const raw = await fetchLcsLeaseOverlay({
        leaseId, horizon, streams, signal: ctl.signal,
      });
      if (tick !== tickRef.current) return; // stale
      const view = buildLcsViewModel(raw);
      setState({ view, raw, loading: false, error: null });
    } catch (e) {
      if (e?.name === "AbortError") return;
      if (tick !== tickRef.current) return;
      setState({ view: null, raw: null, loading: false, error: e?.message || String(e) });
    }
  }, [leaseId, horizon, JSON.stringify(streams || []), enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    run();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [run]);

  return { ...state, refetch: run };
}

export default useLcsLeaseOverlay;
